import { ProviderProgramTaxonomy } from '../../../models/providers/Providers'
import { ProgramCategory, SessionCountByProgram, SessionLimitByProgram } from '../../../models/session/SessionUsage'
import { FOURTEEN_DAYS_TO_MS, SIXTY_DAYS_TO_MS, THIRTY_DAYS_TO_MS } from '../../calendar/types'
import { isBCMProvider, isCLEProvider, isNeurodiversityProvider } from '../../providers/utils'
import { TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND, TREATMENT_OPTIONS_TYPE } from '../constants/treatmentOptions'

export const getProviderAvailabilityFetchDuration = (programTaxonomy?: ProviderProgramTaxonomy[]): number => {
  const taxonomy = programTaxonomy ?? []

  // BCM and CLE providers get 60 days
  if (isBCMProvider({ programTaxonomy: taxonomy }) || isCLEProvider({ programTaxonomy: taxonomy })) {
    return SIXTY_DAYS_TO_MS
  }

  // Neurodiversity providers get 30 days
  if (isNeurodiversityProvider({ programTaxonomy: taxonomy })) {
    return THIRTY_DAYS_TO_MS
  }

  // All other providers get 14 days
  return FOURTEEN_DAYS_TO_MS
}

export function getSessionLimitProgramCategoryByTreatmentOption(
  treatmentOption: TREATMENT_OPTIONS_TYPE,
): ProgramCategory {
  switch (treatmentOption) {
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.COACHING:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.LIVE_MESSAGING_COACHING:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.GUIDED_SELF_CARE:
      return 'coaching'
    default:
      // all other treatment options are considered therapy
      return 'therapy'
  }
}

export const createRemainingSessionCountByProgram = (
  EAPSessionLimitByProgram?: SessionLimitByProgram,
  usedEAPSessionCountByProgram?: SessionCountByProgram,
): SessionCountByProgram => {
  if (!EAPSessionLimitByProgram) {
    return {}
  }
  return Object.keys(EAPSessionLimitByProgram).reduce(
    (sessionsRemainingByProgram: SessionCountByProgram, program: string) => {
      const programCategory = program as ProgramCategory
      const remainingSessionCount =
        EAPSessionLimitByProgram[programCategory] - (usedEAPSessionCountByProgram?.[programCategory] ?? 0)
      sessionsRemainingByProgram[programCategory] = Math.max(remainingSessionCount, 0)
      return sessionsRemainingByProgram
    },
    {} as SessionCountByProgram,
  )
}

export type SessionCountForDisplay = {
  therapySessions: string | number | undefined
  coachingSessions: string | number | undefined
  totalSessions: string | number | undefined
}

export const getSessionCountForDisplay = ({
  numSessions,
  numberOfSessionsByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
}: {
  numSessions?: string
  numberOfSessionsByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
}): SessionCountForDisplay => {
  if (displayVisitsPerIssuePerYearByProgram) {
    const therapy = displayVisitsPerIssuePerYearByProgram.therapy
    const coaching = displayVisitsPerIssuePerYearByProgram.coaching
    return {
      therapySessions: therapy,
      coachingSessions: coaching,
      totalSessions: (therapy || 0) + (coaching || 0),
    }
  }
  if (numberOfSessionsByProgram) {
    const therapy = numberOfSessionsByProgram.therapy
    const coaching = numberOfSessionsByProgram.coaching
    return {
      therapySessions: therapy,
      coachingSessions: coaching,
      totalSessions: (therapy || 0) + (coaching || 0),
    }
  }
  if (displayVisitsPerIssuePerYear) {
    return {
      therapySessions: displayVisitsPerIssuePerYear,
      coachingSessions: displayVisitsPerIssuePerYear,
      totalSessions: displayVisitsPerIssuePerYear,
    }
  }
  return {
    therapySessions: numSessions,
    coachingSessions: numSessions,
    totalSessions: numSessions,
  }
}

export const isProgramAvailableForMinor = (program: ProgramCategory): boolean => program === 'therapy'

export const UNLIMITED_SESSION_LIMIT = 999
export const isUnlimitedSessionLimit = (sessionLimit: number): boolean => sessionLimit === UNLIMITED_SESSION_LIMIT
